/**
 * Client-side JWT payload decode -- for DISPLAY purposes only (e.g. an
 * avatar initial), never as an authorization decision. This does NOT
 * verify the signature; it just reads the already-trusted token this
 * browser tab itself received and is holding.
 *
 * Deliberately reads the ID token, not the access token: Cognito ID
 * tokens are the OIDC-standard place for profile claims (email, etc.);
 * confirmed live that the access token in this pool carries neither
 * `email` nor `username`, only `sub` -- an earlier version of this
 * avatar fell back to sub and showed a meaningless letter from a UUID.
 */
function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const percentEncoded = binary
    .split('')
    .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
    .join('');
  return decodeURIComponent(percentEncoded);
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    return JSON.parse(base64UrlDecode(segment)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Returns the `email` claim from an ID token, or null if absent/undecodable. */
export function getEmailFromIdToken(idToken: string): string | null {
  const payload = decodeJwtPayload(idToken);
  const email = payload?.['email'];
  return typeof email === 'string' ? email : null;
}
